import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'react-toastify';
import { FiMinus, FiPlus, FiX, FiChevronDown, FiShoppingBag } from 'react-icons/fi';
import { optimizeImage } from '../utils/imageOptimizer';
import GCashPaymentSection from '../components/checkout/GCashPaymentSection';
import '../styles/Checkout.css';

// Davao Del Norte data
const zipCodes = {
  "Panabo City": "8105", "Tagum City": "8100", "Island Garden City of Samal": "8119",
  "Carmen": "8101", "Sto. Tomas": "8112", "Kapalong": "8111", "Talaingod": "8117",
  "Asuncion": "8109", "Braulio E. Dujali": "8110", "San Isidro": "8114", "New Corella": "8108"
};

const barangayList = {
  "Panabo City": ["A. O. Floirendo", "Buenavista", "Cacao", "Cagangohan", "Consolacion", "Dapco", "Gredu (Pob.)", "J.P. Laurel", "Kasilak", "Katipunan", "Kauswagan", "Little Panay", "Mabunao", "Maduao", "Malativas", "Manay", "Nanyo", "New Malaga", "New Pandan (Pob.)", "Quezon", "San Francisco (Pob.)", "San Nicolas", "San Pedro", "Santa Cruz", "Santo Niño (Pob.)", "Sindaton", "Tagpore", "Tibungol"],
  "Tagum City": ["Apokon", "Bincungan", "Busaon", "Canocotan", "Cuambogan", "La Filipina", "Liboganon", "Madaum", "Magdum", "Magugpo East", "Magugpo North", "Magugpo Poblacion", "Magugpo South", "Magugpo West", "Mankilam", "New Balamban", "Nueva Fuerza", "Pagsabangan", "Pandapan", "San Agustin", "San Isidro", "Visayan Village"],
  "Island Garden City of Samal": ["Adecor", "Anonang", "Aumbay", "Babak (Pob.)", "Balet", "Cahayag", "Caliclic (Pob.)", "Camudmud", "Catagman", "Cogon", "Cogon (Pob.)", "Dadatan", "Del Monte", "Guilon", "Kanaan", "Kinawitnon", "Libertad", "Libuak", "Liling", "Limba", "Linabuan", "Mambago-A", "Mambago-B", "Miranda (Pob.)", "Moncado (Pob.)", "Pangubatan", "Poblacion (Kaputian)", "Quiola", "San Agustin (Pob.)", "San Antonio", "San Isidro (Babak)", "San Jose (Samal)", "San Miguel (Pob.)", "San Remigio", "Santa Cruz (Pob.)", " some others..."],
  "Carmen": ["Alejal", "Anibongan", "Asuncion", "Cebulano", "Guadalupe", "Ising (Poblacion)", "La Paz", "Mabaus", "Mabuhay", "Magsaysay", "Mangalcal", "Minda", "New Camiling", "Salvacion", "San Isidro", "Santo Niño", "Taba", "Tibulao", "Tubod", "Tuganay"],
  "Sto. Tomas": ["Balagunan", "Bobongon", "Esperanza", "Kimamon", "Kinamayan", "La Libertad", "Lunga-og", "Magwawa", "New Katipunan", "Pantaron", "Salvacion", "San Jose", "San Miguel", "San Vicente", "Talomo", "Tibal-og (Pob.)", "Tulalian", "New Visayas"],
  "Kapalong": ["Gupitan", "Mabantao", "Mamacao", "Pag-asa", "Semong", "Sua-on", "Tibungol", "Katipunan", "Maniki (Pob.)", "San Roque"],
  "Talaingod": ["Dagohoy", "Palma Gil", "Santo Niño"],
  "Asuncion": ["Binancian", "Buan", "Buclad", "Cabaywa", "Camanchiles", "Camansa", "Canatan", "Concepcion", "Doña Andrea", "Magatos", "Napungas", "New Bantayan", "New Santiago", "Pamimon", "Pob. (Cambanogoy)", "Sagayen", "San Vicente", "Santa Filomena", "Sonlon", "Tibungol"],
  "Braulio E. Dujali": ["Cabay-Angan", "Dujali", "Magupising", "New Casay", "Tanglaw"],
  "San Isidro": ["Dacudao", "Datu Balong", "Igangon", "Libuton", "Mamacao", "Pinamuno", "Sabangan", "San Miguel", "San Roque", "Sawata (Poblacion)", "Santo Niño"],
  "New Corella": ["Cabidianan", "Carcor", "Del Monte", "El Salvador", "Limba-an", "Lucob", "Macgum", "Mambing", "Mesaoy", "New Bohol", "New Sambog", "Patrocenio", "Poblacion", "San Roque", "Santa Cruz", "Santa Fe", "Santo Niño", "Suawon", "Ubayon"]
};

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart, updateQuantity, removeFromCart } = useCart();
  const { user } = useAuth();
  const { settings: globalSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [proofImage, setProofImage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [isFirstOrder, setIsFirstOrder] = useState(false);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get('/settings');
        setSettings(data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();

    const checkFirstOrder = async () => {
      try {
        const { data } = await api.get('/orders/check-first-order');
        setIsFirstOrder(data.isFirstOrder);
      } catch (err) {
        console.error('Check first order failed');
      }
    };

    if (user) {
        checkFirstOrder();
    }
  }, [user]);

  const effectiveSettings = settings || globalSettings;

  // Get items array safely
  const items = cart?.items || [];

  const [address, setAddress] = useState({
    street: '', city: '', barangay: '', zipCode: '', landmark: ''
  });

  const [contact, setContact] = useState({
    fullName: user?.name || '',
    phone: '',
    email: user?.email || ''
  });

  const [paymentMethod, setPaymentMethod] = useState('gcash');
  const [shippingMethod, setShippingMethod] = useState('jnt'); // 'jnt' or 'inhouse'


  // Calculate shipping cost
  const discount = isFirstOrder ? cartTotal * 0.10 : 0;
  const subtotalAfterDiscount = cartTotal - discount;

  let shipping = 0;
  if (shippingMethod === 'inhouse') {
    shipping = 50; // Flat rate for in-house delivery
  } else {
    shipping = subtotalAfterDiscount >= 500 ? 0 : 85; // J&T Logic
  }
  const total = subtotalAfterDiscount + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contact.fullName || !contact.phone || !contact.email) {
      toast.error('Please fill in all contact details');
      return;
    }

    if (paymentMethod === 'gcash' && !proofImage) {
      toast.error('Please upload your GCash payment proof');
      return;
    }

    setLoading(true);
    try {
      let response;

      if (paymentMethod === 'gcash') {
        const formData = new FormData();
        formData.append('shippingAddress', JSON.stringify(address));
        formData.append('contactDetails', JSON.stringify(contact));
        formData.append('paymentMethod', 'gcash');
        formData.append('shippingMethod', shippingMethod); // Add shippingMethod
        formData.append('paymentProof', proofImage);
        formData.append('totalAmount', total);
        formData.append('shippingCost', shipping);

        response = await api.post('/orders/gcash', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // COD logic
        const codData = {
          shippingAddress: address,
          contactDetails: contact,
          paymentMethod: 'cod',
          shippingMethod: shippingMethod, // Add shippingMethod
          shippingCost: shipping,
          totalAmount: total
        };

        response = await api.post('/orders/cod', codData);
      }

      await clearCart();
      toast.success(paymentMethod === 'cod' ? 'Order placed! Please prepare exact payment upon delivery.' : 'Order placed! We will confirm once payment is verified.');
      navigate(`/order-success/${response.data._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="checkout-page">
        <div className="empty-checkout">
          <h2>Your cart is empty</h2>
          <Link to="/products">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        {/* Left - Form */}
        <div className="checkout-form-section">
          <div className="checkout-header-group">
            <h1 className="checkout-main-title">Checkout</h1>
            <nav className="checkout-breadcrumb">
              <Link to="/cart">Bag</Link> <span className="breadcrumb-separator">›</span> <span className="active">Checkout</span> <span className="breadcrumb-separator">›</span> <span className="disabled">Confirmation</span>
            </nav>
          </div>

          <form onSubmit={handleSubmit} className="checkout-form">
            {/* Contact */}
            <section className="form-section">
              <h2>Contact</h2>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  required
                />
              </div>
            </section>

            {/* Delivery */}
            <section className="form-section">
              <h2>Delivery</h2>
              <div className="form-group">
                <input
                  placeholder="Full Name"
                  value={contact.fullName}
                  onChange={(e) => setContact({ ...contact, fullName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  placeholder="Phone Number"
                  value={contact.phone}
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  placeholder="Street Address"
                  value={address.street}
                  onChange={(e) => setAddress({ ...address, street: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <select
                  value={address.city}
                  onChange={(e) => {
                    const city = e.target.value;
                    setAddress({ ...address, city, barangay: '', zipCode: zipCodes[city] || '' });
                  }}
                  required
                >
                  <option value="">City/Municipality</option>
                  {Object.keys(zipCodes).map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
                <select
                  value={address.barangay}
                  onChange={(e) => setAddress({ ...address, barangay: e.target.value })}
                  required
                >
                  <option value="">Barangay</option>
                  {address.city && barangayList[address.city]?.map(brgy => (
                    <option key={brgy} value={brgy}>{brgy}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <input placeholder="ZIP Code" value={address.zipCode} readOnly />
                <input
                  placeholder="Landmark (Optional)"
                  value={address.landmark}
                  onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
                />
              </div>
            </section>

            {/* Shipping Method Selection */}
            <section className="form-section">
              <h2>Shipping Method</h2>
              <div className="payment-options-v2">
                <div
                  className={`payment-card-v2 ${shippingMethod === 'jnt' ? 'selected' : ''}`}
                  onClick={() => setShippingMethod('jnt')}
                >
                  <div className="payment-card-v2-header">
                    <div className="payment-radio">
                      <div className="radio-circle"></div>
                    </div>
                    <span>J&T Express 🚚</span>
                    <strong style={{ marginLeft: 'auto' }}>{cartTotal >= 500 ? 'FREE' : '₱85.00'}</strong>
                  </div>
                </div>

                <div
                  className={`payment-card-v2 ${shippingMethod === 'inhouse' ? 'selected' : ''}`}
                  onClick={() => setShippingMethod('inhouse')}
                >
                  <div className="payment-card-v2-header">
                    <div className="payment-radio">
                      <div className="radio-circle"></div>
                    </div>
                    <span>In-House Delivery (Rider) 🛵</span>
                    <strong style={{ marginLeft: 'auto' }}>₱50.00</strong>
                  </div>
                </div>
              </div>
            </section>

            {/* Payment Selection */}
            <section className="form-section">
              <h2>Payment Method</h2>
              <div className="payment-options-v2">
                <div
                  className={`payment-card-v2 ${paymentMethod === 'gcash' ? 'selected' : ''}`}
                  onClick={() => setPaymentMethod('gcash')}
                >
                  <div className="payment-card-v2-header">
                    <div className="payment-radio">
                      <div className="radio-circle"></div>
                    </div>
                    <span>GCash</span>
                    <img
                      src={optimizeImage("/images/payment/gcash-logo.png", 60)}
                      alt="GCash"
                      className="payment-icon-mini"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://res.cloudinary.com/amarace/image/upload/v1/site-assets/gcash_logo.png';
                      }}
                    />
                  </div>
                </div>

                <div
                  className={`payment-card-v2 ${paymentMethod === 'cod' ? 'selected' : ''}`}
                  onClick={() => setPaymentMethod('cod')}
                >
                  <div className="payment-card-v2-header">
                    <div className="payment-radio">
                      <div className="radio-circle"></div>
                    </div>
                    <span>Cash On Delivery (COD)</span>
                    <div className="payment-icon-mini cod-icon">🚚</div>
                  </div>
                </div>
              </div>

              {/* Conditional Payment Details */}
              <div className="payment-details-v2">
                {paymentMethod === 'gcash' ? (
                  <GCashPaymentSection
                    total={total}
                    settings={effectiveSettings}
                    proofImage={proofImage}
                    setProofImage={setProofImage}
                  />
                ) : (
                  <div className="payment-instruction-box cod-box">
                    <div className="cod-info-header">
                      <span className="info-icon">ℹ️</span>
                      <h3>Pay with cash upon delivery</h3>
                    </div>
                    <p className="cod-total-highlight">Order Total: ₱{total.toFixed(2)}</p>

                    <div className="cod-instructions">
                      <ul>
                        <li>Please prepare the exact amount</li>
                        <li>Payment will be collected by our delivery rider</li>
                        <li>Make sure someone is available to receive the order</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </section>

            <button type="submit" className="pay-btn" disabled={loading}>
              {loading ? 'Processing...' : (
                paymentMethod === 'gcash'
                  ? `Pay ₱${total.toFixed(2)}`
                  : `Place Order - COD ₱${total.toFixed(2)}`
              )}
            </button>
          </form>

          <div className="checkout-policies">
            <Link to="/policies/shipping-returns">Refund Policy</Link>
            <Link to="/policies/privacy">Privacy Policy</Link>
            <Link to="/policies/terms">Terms of Service</Link>
          </div>
        </div>

        {/* Right - Order Summary */}
        <div className="checkout-summary-section">
          {/* Mobile Collapsible Toggle Bar */}
          <div
            className="mobile-order-summary-bar"
            onClick={() => setMobileSummaryOpen(!mobileSummaryOpen)}
            role="button"
            tabIndex={0}
          >
            <div className="mobile-summary-left">
              <FiShoppingBag className="summary-bag-icon" />
              <span className="mobile-summary-title">
                {mobileSummaryOpen ? 'Hide Order Summary' : `Your Order · ${items.length} ${items.length === 1 ? 'item' : 'items'}`}
              </span>
              <FiChevronDown className={`summary-arrow ${mobileSummaryOpen ? 'open' : ''}`} />
            </div>
            <div className="mobile-summary-right">
              <strong>₱{total.toFixed(2)}</strong>
            </div>
          </div>

          <div className={`checkout-summary-body ${mobileSummaryOpen ? 'mobile-open' : ''}`}>
            <div className="order-items">
              {items.map((item) => (
                <div key={item.product?._id || item.productId} className="summary-item-modern">
                  <div className="item-image">
                    <img src={optimizeImage(item.product?.images?.[0] || '/placeholder.jpg', 150)} alt={item.product?.name} />
                  </div>

                  <div className="item-details">
                    <h4 className="item-name">{item.product?.name}</h4>
                    <p className="item-price">₱{item.product?.price?.toFixed(2)}</p>

                    <div className="item-controls">
                      <div className="qty-controls">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.product?._id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <FiMinus />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.product?._id, item.quantity + 1)}
                        >
                          <FiPlus />
                        </button>
                      </div>

                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeFromCart(item.product?._id)}
                      >
                        <FiX /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>₱{cartTotal.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="summary-row" style={{ color: '#ff6b6b' }}>
                  <span>First Order Discount (10%)</span>
                  <span>-₱{discount.toFixed(2)}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Shipping</span>
                <span>{shipping === 0 ? 'FREE' : `₱${shipping.toFixed(2)}`}</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>₱{total.toFixed(2)}</span>
              </div>
            </div>

            <div className="accepted-payment-methods">
              <p className="payment-methods-title">WE ACCEPT:</p>
              <div className="payment-icons-row">
                {effectiveSettings?.paymentLogos && effectiveSettings.paymentLogos.length > 0 ? (
                  effectiveSettings.paymentLogos.map((logo, idx) => (
                    <div key={idx} className="payment-method-icon-card">
                      <img
                        src={logo}
                        alt="Payment Method"
                        className="payment-method-icon"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  ))
                ) : (
                  <span style={{ fontSize: '0.8rem', color: '#999' }}>GCash &amp; COD</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
